import Alert from '@/components/Alert/Alert'
import buildScreenHeaderItems from '@/components/buildScreenHeaderItems'
import TableView, { type TableSection } from '@/components/TableView'
import useCopyText from '@/components/useCopyText'
import useWebBackButton from '@/lib/hooks/useWebBackButton'
import { openShare } from '@/lib/share'
import { handleClickToPay } from '@/modules/handleClickToPay'
import { createInviteLinkThunk, destroyInviteLinksThunk, selectInviteLinks } from '@/redux/slices/subAccountSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { PlanCode, UserRole } from '@/typings/domain'
import { Stack } from 'expo-router'
import { keyBy, noop } from 'lodash-es'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Platform } from 'react-native'

const ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.Owner]: '拥有者',
  [UserRole.Admin]: '管理员',
  [UserRole.Worker]: '协作者',
  [UserRole.Outworker]: '查看员',
}

export default function InviteUser() {
  const dispatch = useAppDispatch()
  const copyText = useCopyText()
  const headerLeftItems = useWebBackButton('/profile')

  const inviteLinks = useAppSelector(selectInviteLinks)
  const usageAndBalance = useAppSelector(state => state.usageAndBalance)
  const currentUser = useAppSelector(state => state.currentUser.user)

  const defaultRole = UserRole.Worker
  const isEnabled = inviteLinks.length > 0

  const [selectedRole, setSelectedRole] = useState<UserRole>(inviteLinks[0]?.role ?? defaultRole)

  const availableRoles = useMemo(
    () => [
      { label: ROLE_LABELS[UserRole.Admin], value: UserRole.Admin },
      { label: ROLE_LABELS[UserRole.Worker], value: UserRole.Worker },
      { label: ROLE_LABELS[UserRole.Outworker], value: UserRole.Outworker },
    ],
    []
  )

  const rolesMap = useMemo(() => keyBy(availableRoles, 'value'), [availableRoles])

  const currentInviteLink = useMemo(
    () => inviteLinks.find(link => link.role === selectedRole),
    [inviteLinks, selectedRole]
  )

  const currentInviteLinkUrl = currentInviteLink?.url ?? null

  useEffect(() => {
    if (!isEnabled) return
    if (currentInviteLink) return
    if (!selectedRole) return

    // 检查配额
    const hasAddSubAccountAbility = currentUser?.billingAccountAbilities?.hasAddSubAccountAbility
    const availableQuota = usageAndBalance.usersQuota - usageAndBalance.occupiedUsersCount

    if (!hasAddSubAccountAbility || availableQuota <= 0) {
      // 配额不足，不创建邀请链接
      return
    }

    dispatch(createInviteLinkThunk({ role: selectedRole }))
  }, [currentInviteLink, dispatch, selectedRole, isEnabled, usageAndBalance, currentUser])

  const handleToggleSwitch = useCallback(() => {
    if (isEnabled) {
      Alert.alert('关闭邀请后所有邀请链接将失效，确认关闭？', undefined, [
        { text: '取消', style: 'cancel' },
        {
          text: '关闭',
          style: 'destructive',
          onPress: () => dispatch(destroyInviteLinksThunk()),
        },
      ])
    } else {
      // 检查配额
      const hasAddSubAccountAbility = currentUser?.billingAccountAbilities?.hasAddSubAccountAbility
      const availableQuota = usageAndBalance.usersQuota - usageAndBalance.occupiedUsersCount

      if (!hasAddSubAccountAbility) {
        handleClickToPay({ recommendedPlanCode: PlanCode.E10, currentUser: currentUser ?? undefined })
        return
      }

      if (availableQuota <= 0) {
        Alert.alert(
          '套餐余量不足',
          `当前已使用 ${usageAndBalance.occupiedUsersCount} 个企业成员，${availableQuota < 0 ? `超出配额上限 ${Math.abs(availableQuota)} 个` : '已达到配额上限'}。请禁用其他成员，或者购买更多名额。`,
          [{ text: '我知道了', style: 'cancel' }]
        )
        return
      }

      dispatch(createInviteLinkThunk({ role: defaultRole }))
    }
  }, [dispatch, isEnabled, defaultRole, usageAndBalance, currentUser])

  const menuItems = useMemo(
    () =>
      availableRoles.map(role => ({
        id: role.value,
        title: role.label,
        selected: selectedRole === role.value,
      })),
    [availableRoles, selectedRole]
  )

  const handleMenuItemSelect = useCallback(
    (roleId: string) => {
      setSelectedRole(roleId as UserRole)
    },
    [setSelectedRole]
  )

  const handleCopyInviteLinkPress = useCallback(() => {
    if (!currentInviteLinkUrl) return

    copyText(currentInviteLinkUrl)
  }, [currentInviteLinkUrl, copyText])

  const handleShareInviteLinkPress = useCallback(() => {
    if (!currentInviteLinkUrl) return

    openShare({
      title: '邀请你加入企业',
      url: currentInviteLinkUrl,
      activityItemSources: [
        {
          placeholderItem: { type: 'url', content: currentInviteLinkUrl },
          item: { default: { type: 'url', content: currentInviteLinkUrl } },
          linkMetadata: {
            originalUrl: currentInviteLinkUrl,
            url: currentInviteLinkUrl,
            title: '邀请你加入企业',
          },
        },
      ],
    }).catch(noop)
  }, [currentInviteLinkUrl])

  const sections = useMemo(() => {
    const sections: TableSection[] = []

    // Section 1: Switch for enabling invite links
    sections.push({
      title: '',
      rows: [
        {
          title: '邀请链接',
          type: 'switch',
          switchValue: isEnabled,
          onValueChange: handleToggleSwitch,
        },
      ],
    })

    if (!isEnabled) {
      return sections
    }

    // Section 2: Role selection menu
    if (selectedRole) {
      sections.push({
        title: '',
        rows: [
          {
            title: '邀请受邀人为',
            type: 'menu',
            menuItems,
            menuSelectedTitle: rolesMap[selectedRole]?.label,
            onMenuItemSelect: handleMenuItemSelect,
          },
        ],
      })
    }

    // Section 3: URL display
    sections.push({
      title: '邀请链接',
      rows: [
        {
          title: currentInviteLinkUrl || '',
          subTitle: currentInviteLinkUrl ? null : '加载中...',
        },
      ],
    })

    // Section 4: Action rows
    if (currentInviteLinkUrl) {
      const actionRows = [
        {
          type: 'action' as const,
          icon: 'doc.on.doc',
          title: '复制链接',
          onPress: handleCopyInviteLinkPress,
        },
      ]

      if (Platform.OS !== 'web') {
        actionRows.push({
          type: 'action' as const,
          icon: 'square.and.arrow.up',
          title: '分享链接',
          onPress: handleShareInviteLinkPress,
        })
      }

      sections.push({
        title: '',
        rows: actionRows,
      })
    }

    return sections
  }, [
    isEnabled,
    selectedRole,
    menuItems,
    rolesMap,
    currentInviteLinkUrl,
    handleToggleSwitch,
    handleMenuItemSelect,
    handleCopyInviteLinkPress,
    handleShareInviteLinkPress,
  ])

  return (
    <>
      <Stack.Screen options={{ title: '邀请成员', ...buildScreenHeaderItems({ left: headerLeftItems }) }} />
      <TableView
        sections={sections}
        headerText="通过链接邀请他人加入企业"
        footerText={isEnabled && currentInviteLinkUrl ? '链接长期有效，关闭邀请后立即失效' : undefined}
      />
    </>
  )
}
